import { Stack, Text } from '@chakra-ui/react'
import { DateTime } from 'luxon'
import { useMemo } from 'react'

import { isNotNull, joinWithConjunction, noOp } from '@/utils/helpers'
import toEnumValueOrNull from '@/utils/toEnumValueOrNull'

import PlatformFeedbackQ from '@/queries/PlatformFeedbackQ'
import SessionQ from '@/queries/SessionQ'
import TopicQ from '@/queries/TopicQ'
import UserQ from '@/queries/UserQ'
import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import type { Query } from '@/client/query'
import { RPCReload } from '@/client/rpc'
import { SafetyReportStatus } from '@/client/services/safetyReport'

import { useToaster } from '@/components/Toast/useToaster'
import Form from '@/components/UI/Form/Form'
import type { FormInputType, FormValues } from '@/components/UI/Form/types'
import ModalV3 from '@/components/UI/Modal/ModalV3'

export default function ReviewRemovedLearnerModal({
  safetyReport,
}: {
  safetyReport: Query
}): JSX.Element | null {
  const toast = useToaster()
  const user = useUser()

  const safetyReportId = safetyReport(PlatformFeedbackQ.id)

  const session = safetyReport.one(PlatformFeedbackQ.getSession())
  const sessionTitle = session(SessionQ.title)
  const sessionStartDate =
    session(SessionQ.start_date) != null
      ? DateTime.fromISO(`${session(SessionQ.start_date)}`)
      : null

  const topic = session.one(SessionQ.getTopic())
  const topicName = topic(TopicQ.name)

  const subjectUsers = safetyReport.many(PlatformFeedbackQ.getSubjectUsers())
  const subjectUserNames = subjectUsers
    .map(subjectUser => subjectUser(UserQ.display_name))
    .filter(isNotNull)

  const [handleChangeSafetyStatus] = useAPIWrapper(
    async (values: FormValues) => {
      if (safetyReportId == null) {
        return
      }

      const status = toEnumValueOrNull(SafetyReportStatus, values.intention)
      if (status == null) {
        throw new Error('Invalid status')
      }

      await RPCReload.admin_addSafetyReportAction({
        id: safetyReportId,
        status,
        comment: `${values.explanation}`,
        unsuspendSubjectUsers: values.intention === SafetyReportStatus.Resolved,
      })

      toast({
        title: 'Successfully reviewed removed learner(s)',
        status: 'success',
      })
    },
    [safetyReportId, toast],
    'Change safety status'
  )

  const formInputs: FormInputType[] = useMemo(
    () => [
      {
        type: 'radio',
        label: 'Did you have an issue with this learner and intend to suspend their account?',
        name: 'intention',
        required: true,
        options: [
          {
            label: 'Yes',
            value: SafetyReportStatus.NeedsAction,
          },
          {
            label: 'No',
            value: SafetyReportStatus.Resolved,
          },
        ],
        size: 6,
      },
      {
        type: 'textarea',
        label: 'Please provide an explanation',
        name: 'explanation',
        required: true,
        size: 6,
      },
    ],
    []
  )

  return (
    <ModalV3
      open
      size="xl"
      title="Review Removed Learner"
      onClose={noOp}
      disableClose
      disableClickOutside
      body={() => (
        <Stack direction="column" spacing={4}>
          <Text>Hi {user.firstName}!</Text>
          <Text>
            It looks like you removed {joinWithConjunction(subjectUserNames)} from your{' '}
            {sessionTitle ?? topicName} session on{' '}
            {sessionStartDate?.toFormat("MMMM d, yyyy 'at' h:mm a")}.
          </Text>
          <Text>
            As per Schoolhouse policies, removing a learner from a session suspends their account.
            Please fill out this form to confirm whether this removal was accidental, fixed a
            technical issue, or if there was a safety issue our team can help out with. Thanks for
            helping keep the Schoolhouse community safe!
          </Text>

          <Form name="SafetyReport" blocks={formInputs} onSubmit={handleChangeSafetyStatus} />
        </Stack>
      )}
    />
  )
}
